import { SyntheticEvent } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

export interface ShareProposalActionProps {
  proposalId: string;
  onError?: (error: Error) => void;
  onClickBeforeCopy?: () => Promise<void>;
  onSuccess?: (copiedContent: string) => void;
  ActionComponent: (props: {
    onClick: (e: SyntheticEvent) => Promise<void>;
  }) => JSX.Element;
}

// The link opens the proposal page in the app. The recipient must sign in and
// have access to the household to see the proposal.
function getProposalURL(proposalId: string): string {
  const proposalPath = getCompletePathFromRouteKey(
    ROUTE_KEYS.HOUSEHOLD_PROPOSAL_PRESENTATION,
    { proposalId }
  );
  return new URL(proposalPath, window.location.origin).toString();
}

// this is effectively an HOC -- it takes component and passes it an onClick property,
// and abstracts away the handling of the share link and error handling
export function ShareProposalAction({
  proposalId,
  onError,
  onSuccess,
  onClickBeforeCopy,
  ActionComponent,
}: ShareProposalActionProps) {
  const trackUserEvent = useTrackUserEvent();
  const [copyToClipboard] = useCopyToClipboard();
  const { showFeedback } = useFeedback();

  function handleOnSuccess(copiedContent: string) {
    showFeedback('Link copied to your clipboard!', {
      variant: 'success',
    });
    trackUserEvent('proposal share link copied', {
      proposalId,
    });
    onSuccess && onSuccess(copiedContent);
  }

  function handleOnError(error: Error) {
    showFeedback('Failed to copy the link', {
      variant: 'error',
    });
    onError && onError(error);
  }

  async function handleShareProposalClick(e: SyntheticEvent) {
    // prevent the click from bubbling up to the parent element
    // (e.g., proposal button on a clickable table row triggers the row click)
    e.stopPropagation();
    // this exists to support the "save & copy to clipboard" behavior in the proposal builder.
    // it's kind of messy, but hopefully will go away before our pilot launch and be replaced
    // with more robust sharing functionality.
    if (onClickBeforeCopy) {
      await onClickBeforeCopy();
    }

    copyToClipboard(getProposalURL(proposalId), handleOnSuccess, handleOnError);
  }

  return <ActionComponent onClick={handleShareProposalClick} />;
}
